import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FiLogOut, FiArrowLeft, FiChevronRight } from "react-icons/fi";
import { toast } from "react-toastify";
import { supabase, getSafeSession, getCachedSession, clearSupabaseAuthStorage } from "../supabaseClient"; 
import "./MasterAdmin.css";
import SupportMessagesAdmin from "./Components/SupportMessagesAdmin";


// ─── DATA ────────────────────────────────────────────────────────────────────


const CLIENT_DEFAULTS = [
  {
    id: 1,
    name: "Garcia Law Firm",
    plan: "Growth",
    status: "Active",
    tasks: 3,
    color: "#7c5cfc",
    joined: "Jan 2026",
  },
  {
    id: 2,
    name: "CityDent Clinic",
    plan: "Core",
    status: "Active",
    tasks: 1,
    color: "#00d4ff",
    joined: "Feb 2026",
  },
  {
    id: 3,
    name: "HomeGuard HVAC",
    plan: "Expansion",
    status: "Active",
    tasks: 5,
    color: "#00e5a0",
    joined: "Mar 2026",
  },
];

const TASK_DEFAULTS = {
  todo: [
    {
      id: 1,
      title: "Schema markup for Garcia Law",
      client: "Garcia Law Firm",
      assignee: 1,
      priority: "high",
    },
    {
      id: 2,
      title: "Robots.txt audit for Realty Plus",
      client: "Realty Plus",
      assignee: null,
      priority: "medium",
    },
    {
      id: 3,
      title: "Competitor gap analysis",
      client: "FinEdge Advisors",
      assignee: 2,
      priority: "low",
    },
  ],
  inprogress: [
    {
      id: 4,
      title: "GPTBot crawler fix",
      client: "CityDent Clinic",
      assignee: 1,
      priority: "high",
    },
    {
      id: 5,
      title: "Content structuring Q2",
      client: "HomeGuard HVAC",
      assignee: 3,
      priority: "medium",
    },
  ],
  done: [
    {
      id: 6,
      title: "Initial AI audit report",
      client: "Garcia Law Firm",
      assignee: 2,
      priority: "low",
    },
    {
      id: 7,
      title: "Business entity optimization",
      client: "HomeGuard HVAC",
      assignee: 4,
      priority: "medium",
    },
  ],
};

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function initials(name) {
  if (!name || typeof name !== "string") return "NA";
  return (
    name
      .trim()
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase() || "NA"
  );
}

const COMPANY_COLORS = ["#7c5cfc", "#00d4ff", "#00e5a0", "#ff7b47", "#ff4d6a", "#f0c040"];
function getCompanyColor(name) {
  if (!name) return COMPANY_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return COMPANY_COLORS[Math.abs(hash) % COMPANY_COLORS.length];
}

function formatScanDate(dateStr) {
  if (!dateStr) return "N/A";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  } catch (e) {
    return dateStr;
  }
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffSecs = Math.floor((now - d) / 1000);
    if (diffSecs < 60) return "Just now";
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 30) return `${diffDays}d ago`;
    const diffMonths = Math.floor(diffDays / 30);
    return `${diffMonths}mo ago`;
  } catch (e) {
    return "";
  }
}

function exportGuestScansCSV(scans) {
  if (!scans || scans.length === 0) {
    toast.info("No guest scan records to export.");
    return;
  }
  const headers = [
    "ID",
    "Company Name",
    "Website URL",
    "Date Scanned (UTC)",
    "Description",
    "Business Type",
    "Multiple Locations",
    "Display Pricing",
    "AI Readiness Score",
  ];
  const rows = scans.map((s) => [
    `"${(s.id || "").replace(/"/g, '""')}"`,
    `"${(s.company || "").replace(/"/g, '""')}"`,
    `"${(s.website || "").replace(/"/g, '""')}"`,
    `"${(s.created_at || "").replace(/"/g, '""')}"`,
    `"${(s.description || "").replace(/"/g, '""')}"`,
    `"${(s.business_type || "").replace(/"/g, '""')}"`,
    `"${(s.multiple_locations || "").replace(/"/g, '""')}"`,
    `"${(s.display_pricing || "").replace(/"/g, '""')}"`,
    s.overall_score !== null && s.overall_score !== undefined ? s.overall_score : "",
  ]);

  const csvContent =
    "data:text/csv;charset=utf-8," +
    [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute(
    "download",
    `seenbyai_guest_scans_${new Date().toISOString().slice(0, 10)}.csv`
  );
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  toast.success("Guest scans exported to CSV!");
}

const priorityBadge = {
  high: "badge-red",
  medium: "badge-orange",
  low: "badge-neutral",
};
const statusBadge = {
  Active: "badge-green",
  Pending: "badge-orange",
  Paused: "badge-neutral",
};

// ─── MODAL ───────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children, footer }) {
  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ─── PLANS TAB ───────────────────────────────────────────────────────────────
function PlansTab({ plans, onSavePlan, onDeletePlan, onCreatePlan }) {
  const [editingPlan, setEditingPlan] = useState(null);
  const [newFeature, setNewFeature] = useState("");
  const [addingPlan, setAddingPlan] = useState(false);
  const [newPlan, setNewPlan] = useState({ name: "", price: "", features: [] });
  const [newPlanFeature, setNewPlanFeature] = useState("");

  const openEdit = (plan) =>
    setEditingPlan({ ...plan, price: String((plan.price / 100).toFixed(2)) });

  const saveEdit = () => {
    onSavePlan({
      ...editingPlan,
      price: Math.round(parseFloat(editingPlan.price) * 100),
    });
    setEditingPlan(null);
    setNewFeature("");
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setEditingPlan({
      ...editingPlan,
      features: [...editingPlan.features, newFeature.trim()],
    });
    setNewFeature("");
  };

  const removeFeature = (i) => {
    setEditingPlan({
      ...editingPlan,
      features: editingPlan.features.filter((_, idx) => idx !== i),
    });
  };

  const deletePlan = (id) => onDeletePlan(id);

  const createPlan = () => {
    if (!newPlan.name || !newPlan.price) return;
    onCreatePlan({
      ...newPlan,
      price: Math.round(parseFloat(newPlan.price) * 100),
    });
    setAddingPlan(false);
    setNewPlan({ name: "", price: "", features: [] });
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Plan Management</div>
            <div className="card-sub">
              Adjust pricing tiers and included features
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setAddingPlan(true)}
          >
            ＋ New Plan
          </button>
        </div>
        <div className="card-body">
          <div className="plans-grid">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`plan-card${plan.popular ? " popular" : ""}`}
              >
                {plan.popular && (
                  <div className="plan-popular-tag">MOST POPULAR</div>
                )}
                <div className="plan-name">{plan.name}</div>
                <div className="plan-price-row">
                  <span className="plan-price">${(plan.price / 100).toFixed(2)}</span>
                  <span className="plan-price-period">/mo</span>
                </div>
                <div className="plan-features">
                  {plan.features.map((f, i) => (
                    <div key={i} className="plan-feature">
                      <span>✦</span>
                      <span style={{ flex: 1, marginLeft: 6, fontSize: 11 }}>
                        {f}
                      </span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    style={{ flex: 1 }}
                    onClick={() => openEdit(plan)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-danger btn-sm btn-icon"
                    onClick={() => deletePlan(plan.id)}
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit Plan Modal */}
      {editingPlan && (
        <Modal
          title={`Edit "${editingPlan.name}" Plan`}
          onClose={() => setEditingPlan(null)}
          footer={
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setEditingPlan(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveEdit}>
                Save Changes
              </button>
            </>
          }
        >
          <div className="input-group">
            <label className="input-label">Plan Name</label>
            <input
              className="input"
              value={editingPlan.name}
              onChange={(e) =>
                setEditingPlan({ ...editingPlan, name: e.target.value })
              }
            />
          </div>
          <div className="input-group">
            <label className="input-label">Monthly Price (USD)</label>
            <div className="price-editor-row">
              <span style={{ fontSize: 18, color: "var(--accent-cyan)" }}>
                $
              </span>
              <input
                className="input"
                type="number"
                value={editingPlan.price}
                onChange={(e) =>
                  setEditingPlan({ ...editingPlan, price: e.target.value })
                }
              />
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                /month
              </span>
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">Features</label>
            <div className="feature-list" style={{ marginBottom: 8 }}>
              {editingPlan.features.map((f, i) => (
                <div key={i} className="feature-pill">
                  {f}
                  <button
                    className="feature-pill-remove"
                    onClick={() => removeFeature(i)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                placeholder="Add feature..."
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addFeature()}
              />
              <button className="btn btn-ghost btn-sm" onClick={addFeature}>
                Add
              </button>
            </div>
          </div>
          <div className="input-group">
            <label className="input-label">Popular Plan?</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[true, false].map((v) => (
                <button
                  key={String(v)}
                  className={`btn btn-sm ${editingPlan.popular === v ? "btn-primary" : "btn-ghost"}`}
                  onClick={() => setEditingPlan({ ...editingPlan, popular: v })}
                >
                  {v ? "Yes — Mark Popular" : "No"}
                </button>
              ))}
            </div>
          </div>
        </Modal>
      )}

      {/* Add Plan Modal */}
      {addingPlan && (
        <Modal
          title="Create New Plan"
          onClose={() => setAddingPlan(false)}
          footer={
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setAddingPlan(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={createPlan}>
                Create Plan
              </button>
            </>
          }
        >
          <div className="input-group">
            <label className="input-label">Plan Name</label>
            <input
              className="input"
              placeholder="e.g. Pro"
              value={newPlan.name}
              onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
            />
          </div>
          <div className="input-group">
            <label className="input-label">Monthly Price (USD)</label>
            <input
              className="input"
              type="number"
              placeholder="e.g. 699"
              value={newPlan.price}
              onChange={(e) =>
                setNewPlan({ ...newPlan, price: e.target.value })
              }
            />
          </div>
          <div className="input-group">
            <label className="input-label">Features (press Enter to add)</label>
            <div className="feature-list" style={{ marginBottom: 8 }}>
              {newPlan.features.map((f, i) => (
                <div key={i} className="feature-pill">
                  {f}
                  <button
                    className="feature-pill-remove"
                    onClick={() =>
                      setNewPlan({
                        ...newPlan,
                        features: newPlan.features.filter(
                          (_, idx) => idx !== i,
                        ),
                      })
                    }
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="input"
                style={{ flex: 1 }}
                placeholder="Add feature..."
                value={newPlanFeature}
                onChange={(e) => setNewPlanFeature(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && newPlanFeature.trim()) {
                    setNewPlan({
                      ...newPlan,
                      features: [...newPlan.features, newPlanFeature.trim()],
                    });
                    setNewPlanFeature("");
                  }
                }}
              />
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  if (newPlanFeature.trim()) {
                    setNewPlan({
                      ...newPlan,
                      features: [...newPlan.features, newPlanFeature.trim()],
                    });
                    setNewPlanFeature("");
                  }
                }}
              >
                Add
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── TASKS TAB ───────────────────────────────────────────────────────────────
function TasksTab({ clients, tasks, team, onAddTask, onMoveTask, onReassignTask, onDeleteTask }) {
  const [addModal, setAddModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "",
    client: "",
    assignee: "",
    priority: "medium",
    status: "todo",
  });

  const allTasks = [...(tasks.todo || []), ...(tasks.inprogress || []), ...(tasks.done || [])];

  const addTask = () => {
    if (!newTask.title) return;
    onAddTask(newTask);
    setAddModal(false);
    setNewTask({
      title: "",
      client: "",
      assignee: "",
      priority: "medium",
      status: "todo",
    });
  };

  const moveTask = (task, from, to) => {
    onMoveTask(task, from, to);
  };

  const reassign = (task, col, memberId) => {
    onReassignTask(task, col, memberId);
  };

  const renderCol = (col, label, _accentClass) => (
    <div>
      <div className="task-col-header">
        <span className="task-col-title">{label}</span>
        <span className="task-col-count">{tasks[col].length}</span>
      </div>
      {tasks[col].map((task) => {
        const member = team.find((m) => m.id === task.assignee);
        return (
          <div key={task.id} className="task-card">
            <div className="task-card-title">{task.title}</div>
            {task.client && (
              <div className="task-client" style={{ marginBottom: 8 }}>
                📁 {task.client}
              </div>
            )}
            <div className="task-card-meta">
              <span className={`badge ${priorityBadge[task.priority]}`}>
                {task.priority}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div
                  className="task-assignee"
                  style={{ background: member?.color || "var(--border-light)" }}
                >
                  {member ? initials(member.name) : "?"}
                </div>
                <select
                  className="input"
                  style={{ padding: "2px 6px", fontSize: 10, height: 22 }}
                  value={task.assignee || ""}
                  onChange={(e) =>
                    reassign(
                      task,
                      col,
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                >
                  <option value="">Unassigned</option>
                  {team.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ display: "flex", gap: 4, marginTop: 8, alignItems: "center" }}>
              {col !== "todo" && (
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ fontSize: 10, padding: "2px 8px" }}
                  onClick={() =>
                    moveTask(
                      task,
                      col,
                      col === "inprogress" ? "todo" : "inprogress",
                    )
                  }
                >
                  ← Back
                </button>
              )}
              {col !== "done" && (
                <button
                  className="btn btn-primary btn-sm"
                  style={{ fontSize: 10, padding: "2px 8px" }}
                  onClick={() =>
                    moveTask(task, col, col === "todo" ? "inprogress" : "done")
                  }
                >
                  → Move
                </button>
              )}
              <button
                className="btn btn-danger btn-sm btn-icon"
                style={{ fontSize: 10, padding: "2px 6px", marginLeft: "auto", minWidth: 24, height: 24 }}
                onClick={() => onDeleteTask(task.id, col)}
              >
                🗑
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Task Board</div>
            <div className="card-sub">
              Assign and track work across the team · {allTasks.length} total
              tasks
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setAddModal(true)}
          >
            ＋ Add Task
          </button>
        </div>
        <div className="card-body">
          <div className="task-columns">
            {renderCol("todo", "To Do", "badge-neutral")}
            {renderCol("inprogress", "In Progress", "badge-orange")}
            {renderCol("done", "Done", "badge-green")}
          </div>
        </div>
      </div>

      {addModal && (
        <Modal
          title="New Task"
          onClose={() => setAddModal(false)}
          footer={
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setAddModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={addTask}>
                Create Task
              </button>
            </>
          }
        >
          <div className="input-group">
            <label className="input-label">Task Title</label>
            <input
              className="input"
              placeholder="e.g. Fix robots.txt crawl block"
              value={newTask.title}
              onChange={(e) =>
                setNewTask({ ...newTask, title: e.target.value })
              }
            />
          </div>
          <div className="input-group">
            <label className="input-label">Client (optional)</label>
            <select
              className="input"
              value={newTask.client}
              onChange={(e) =>
                setNewTask({ ...newTask, client: e.target.value })
              }
            >
              <option value="">— No client —</option>
              {clients.map((c) => (
                <option key={c.id} value={c.name}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Assign To</label>
            <select
              className="input"
              value={newTask.assignee}
              onChange={(e) =>
                setNewTask({ ...newTask, assignee: e.target.value })
              }
            >
              <option value="">Unassigned</option>
              {team.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} — {m.role}
                </option>
              ))}
            </select>
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div className="input-group">
              <label className="input-label">Priority</label>
              <select
                className="input"
                value={newTask.priority}
                onChange={(e) =>
                  setNewTask({ ...newTask, priority: e.target.value })
                }
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="input-group">
              <label className="input-label">Status</label>
              <select
                className="input"
                value={newTask.status}
                onChange={(e) =>
                  setNewTask({ ...newTask, status: e.target.value })
                }
              >
                <option value="todo">To Do</option>
                <option value="inprogress">In Progress</option>
                <option value="done">Done</option>
              </select>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── CLIENTS TAB ─────────────────────────────────────────────────────────────
// Updated to accept clients and setClients as props so data stays in sync
function ClientsTab({ clients, onAddClient, team, onAddClientTask, allTasks, plans, onSelectClient }) {
  const [addModal, setAddModal] = useState(false);
  const [taskModal, setTaskModal] = useState(null);
  const [newClient, setNewClient] = useState({
    name: "",
    plan: "",
    status: "Active",
  });
  const [clientTask, setClientTask] = useState({
    title: "",
    assignee: "",
    priority: "medium",
  });

  useEffect(() => {
    if (plans && plans.length > 0 && !newClient.plan) {
      setNewClient((prev) => ({ ...prev, plan: plans[0].name }));
    }
  }, [plans, newClient.plan]);

  // Group allTasks by client name dynamically
  const clientTasksMap = {};
  allTasks.forEach((t) => {
    const key = t.client;
    if (key) {
      const lowerKey = key.toLowerCase();
      if (!clientTasksMap[lowerKey]) {
        clientTasksMap[lowerKey] = [];
      }
      clientTasksMap[lowerKey].push(t);
    }
  });

  const addClient = () => {
    if (!newClient.name) return;
    const finalPlan = newClient.plan || (plans[0]?.name || "");
    onAddClient({ ...newClient, plan: finalPlan });
    setAddModal(false);
    setNewClient({ name: "", plan: plans[0]?.name || "", status: "Active" });
  };

  const addClientTask = () => {
    if (!clientTask.title || !taskModal) return;
    onAddClientTask(taskModal.name, clientTask);
    setClientTask({ title: "", assignee: "", priority: "medium" });
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Clients</div>
            <div className="card-sub">
              {clients.length} total clients ·{" "}
              {clients.filter((c) => c.status === "Active").length} active
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setAddModal(true)}
          >
            ＋ Add Client
          </button>
        </div>
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Plan</th>
                <th>Status</th>
                <th>Tasks</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="client-name-cell">
                      <div
                        className="client-avatar"
                        style={{ background: c.color }}
                      >
                        {initials(c.name)}
                      </div>
                      <span style={{ fontWeight: 600 }}>{c.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-cyan">{c.plan}</span>
                  </td>
                  <td>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <div
                        className={`dot ${c.status === "Active" ? "dot-green" : c.status === "Pending" ? "dot-orange" : "dot-muted"}`}
                      ></div>
                      <span
                        className={`badge ${statusBadge[c.status] || "badge-neutral"}`}
                      >
                        {c.status}
                      </span>
                    </div>
                  </td>
                  <td>
                    <span
                      style={{ fontWeight: 700, color: "var(--accent-cyan)" }}
                    >
                      {clientTasksMap[c.name.toLowerCase()]?.length || 0}
                    </span>
                  </td>
                  <td>
                    <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
                      {c.joined}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 6 }}>
                      {onSelectClient && (
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => onSelectClient(c)}
                        >
                          Manage
                        </button>
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setTaskModal(c)}
                      >
                        ＋ Task
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {addModal && (
        <Modal
          title="Add New Client"
          onClose={() => setAddModal(false)}
          footer={
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setAddModal(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={addClient}>
                Add Client
              </button>
            </>
          }
        >
          <div className="input-group">
            <label className="input-label">Business Name</label>
            <input
              className="input"
              placeholder="e.g. Smith Dental Practice"
              value={newClient.name}
              onChange={(e) =>
                setNewClient({ ...newClient, name: e.target.value })
              }
            />
          </div>
          <div className="input-group">
            <label className="input-label">Plan</label>
            <select
              className="input"
              value={newClient.plan}
              onChange={(e) =>
                setNewClient({ ...newClient, plan: e.target.value })
              }
            >
              {plans.length === 0 ? (
                <option value="">No plans available</option>
              ) : (
                plans.map((p) => (
                  <option key={p.id} value={p.name}>
                    {p.name}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="input-group">
            <label className="input-label">Status</label>
            <select
              className="input"
              value={newClient.status}
              onChange={(e) =>
                setNewClient({ ...newClient, status: e.target.value })
              }
            >
              <option>Active</option>
              <option>Pending</option>
              <option>Paused</option>
            </select>
          </div>
        </Modal>
      )}

      {taskModal && (
        <Modal
          title={`Tasks — ${taskModal.name}`}
          onClose={() => setTaskModal(null)}
          footer={
            <button
              className="btn btn-ghost"
              onClick={() => setTaskModal(null)}
            >
              Close
            </button>
          }
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {(clientTasksMap[taskModal.name.toLowerCase()] || []).length === 0 && (
              <div
                style={{
                  color: "var(--text-muted)",
                  fontSize: 13,
                  textAlign: "center",
                  padding: "12px 0",
                }}
              >
                No tasks yet for this client.
              </div>
            )}
            {(clientTasksMap[taskModal.name.toLowerCase()] || []).map((t) => {
              const member = team.find((m) => m.id === Number(t.assignee));
              return (
                <div
                  key={t.id}
                  style={{
                    background: "var(--bg-elevated)",
                    borderRadius: 8,
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    {t.title}
                  </span>
                  <div
                    style={{ display: "flex", gap: 6, alignItems: "center" }}
                  >
                    <span className={`badge ${priorityBadge[t.priority]}`}>
                      {t.priority}
                    </span>
                    {member && (
                      <div
                        className="task-assignee"
                        style={{ background: member.color }}
                      >
                        {initials(member.name)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="divider" />
          <div
            style={{
              marginTop: 14,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "var(--text-muted)",
                letterSpacing: "0.5px",
              }}
            >
              ADD TASK FOR {taskModal.name.toUpperCase()}
            </div>
            <input
              className="input"
              placeholder="Task title..."
              value={clientTask.title}
              onChange={(e) =>
                setClientTask({ ...clientTask, title: e.target.value })
              }
            />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <select
                className="input"
                value={clientTask.assignee}
                onChange={(e) =>
                  setClientTask({ ...clientTask, assignee: e.target.value })
                }
              >
                <option value="">Unassigned</option>
                {team.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
              <select
                className="input"
                value={clientTask.priority}
                onChange={(e) =>
                  setClientTask({ ...clientTask, priority: e.target.value })
                }
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <button
              className="btn btn-primary btn-sm"
              style={{ alignSelf: "flex-start" }}
              onClick={addClientTask}
            >
              ＋ Add Task
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── USERS TAB ───────────────────────────────────────────────────────────────
function UsersTab({ users, onSelectClient, loading, error, onRefresh }) {
  if (loading) {
    return (
      <div className="card" style={{ padding: "60px", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: "16px" }}>
        <div className="spinner"></div>
        <div style={{ color: "var(--text-secondary)", fontWeight: 500, fontSize: "14px" }}>Loading users database...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card" style={{ padding: "60px", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: "20px", textAlign: "center" }}>
        <div style={{ fontSize: "28px" }}>⚠️</div>
        <div style={{ color: "var(--text-secondary)", fontWeight: 500, fontSize: "14px" }}>{error}</div>
        <button className="btn btn-primary" onClick={onRefresh} style={{ padding: "8px 16px" }}>
          Retry Fetch
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="card">
        <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="card-title">Users</div>
            <div className="card-sub">
              {users.length} total users · click a user row to assign admin roles
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onRefresh} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <span>↻</span> Refresh
          </button>
        </div>
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email Address</th>
                <th>Plan</th>
                <th>Role</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "40px", color: "var(--text-secondary)" }}>
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="clickable-row" onClick={() => onSelectClient(u)}>
                    <td>
                      <div className="client-name-cell">
                        <div
                          className="client-avatar"
                          style={{ background: u.color }}
                        >
                          {initials(u.name)}
                        </div>
                        <span style={{ fontWeight: 600 }}>{u.name}</span>
                      </div>
                    </td>
                    <td>
                      <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>{u.email || "No email info"}</span>
                    </td>
                    <td>
                      <span className="badge badge-cyan">
                        {u.plan || "Guest"}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${u.is_admin ? "badge-purple" : "badge-neutral"}`}>
                        {u.is_admin ? "Admin" : "User"}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: "var(--text-muted)", fontSize: 12 }}>
                        {u.joined}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── GUEST SCANS TAB ──────────────────────────────────────────────────────────
function GuestScansTab({
  guestScans,
  loading,
  error,
  onRefresh,
  onSelectScan,
  onDeleteScan,
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredScans = guestScans.filter((s) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (s.company && s.company.toLowerCase().includes(q)) ||
      (s.website && s.website.toLowerCase().includes(q)) ||
      (s.description && s.description.toLowerCase().includes(q)) ||
      (s.business_type && s.business_type.toLowerCase().includes(q))
    );
  });

  const now = new Date();
  const currentMonth = now.getMonth();
  const currentYear = now.getFullYear();
  const thisMonthCount = guestScans.filter((s) => {
    if (!s.created_at) return false;
    const d = new Date(s.created_at);
    return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
  }).length;

  const todayCount = guestScans.filter((s) => {
    if (!s.created_at) return false;
    const d = new Date(s.created_at);
    return (
      d.getDate() === now.getDate() &&
      d.getMonth() === now.getMonth() &&
      d.getFullYear() === now.getFullYear()
    );
  }).length;

  const validScores = guestScans
    .map((s) => s.overall_score)
    .filter((v) => typeof v === "number" && !isNaN(v));
  const avgScore =
    validScores.length > 0
      ? Math.round(validScores.reduce((a, b) => a + b, 0) / validScores.length)
      : null;

  if (loading) {
    return (
      <div
        className="card"
        style={{
          padding: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div className="spinner"></div>
        <div
          style={{
            color: "var(--text-secondary)",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          Loading guest scanner database...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="card"
        style={{
          padding: "60px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          gap: "20px",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "28px" }}>⚠️</div>
        <div
          style={{
            color: "var(--text-secondary)",
            fontWeight: 500,
            fontSize: "14px",
          }}
        >
          {error}
        </div>
        <button
          className="btn btn-primary"
          onClick={onRefresh}
          style={{ padding: "8px 16px" }}
        >
          Retry Fetch
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="stats-row" style={{ marginBottom: "20px" }}>
        <div className="stat-card orange">
          <div className="stat-icon orange">⚡</div>
          <div className="stat-value">{guestScans.length}</div>
          <div className="stat-label">Total Guest Scans</div>
        </div>
        <div className="stat-card cyan">
          <div className="stat-icon cyan">📅</div>
          <div className="stat-value">{thisMonthCount}</div>
          <div className="stat-label">Scans This Month</div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon green">🕒</div>
          <div className="stat-value">{todayCount}</div>
          <div className="stat-label">Scans Today</div>
        </div>
        <div className="stat-card purple">
          <div className="stat-icon purple">🎯</div>
          <div className="stat-value">{avgScore !== null ? `${avgScore}%` : "N/A"}</div>
          <div className="stat-label">Avg AI Readiness Score</div>
        </div>
      </div>

      <div className="card">
        <div
          className="card-header"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <div className="card-title">Guest Scanner Leads</div>
            <div className="card-sub">
              {guestScans.length} users scanned without logging in · track company names, URLs, dates & descriptions
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div style={{ position: "relative" }}>
              <input
                type="text"
                className="input"
                placeholder="Search company, URL, desc..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: "230px", fontSize: "12px", paddingRight: searchQuery ? "28px" : "12px" }}
              />
              {searchQuery && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: "6px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: "2px 6px",
                    border: "none",
                    background: "none",
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            <button
              className="btn btn-ghost btn-sm"
              onClick={() => exportGuestScansCSV(guestScans)}
              title="Download guest scan records as CSV"
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <span>📥</span> Export CSV
            </button>

            <button
              className="btn btn-ghost btn-sm"
              onClick={onRefresh}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <span>↻</span> Refresh
            </button>
          </div>
        </div>

        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th style={{ minWidth: "160px" }}>Company Name</th>
                <th style={{ minWidth: "200px" }}>Website URL</th>
                <th style={{ minWidth: "160px" }}>Date & Time</th>
                <th style={{ minWidth: "260px" }}>Company Description</th>
                <th style={{ minWidth: "110px" }}>Business Type</th>
                <th style={{ minWidth: "90px" }}>Score</th>
                <th style={{ textAlign: "right", minWidth: "110px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredScans.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    style={{
                      textAlign: "center",
                      padding: "48px 20px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {searchQuery
                      ? `No guest scans matching "${searchQuery}".`
                      : "No guest scan records found."}
                  </td>
                </tr>
              ) : (
                filteredScans.map((scan) => {
                  const companyName = scan.company || "Unknown";
                  const avatarColor = getCompanyColor(companyName);
                  const formattedDate = formatScanDate(scan.created_at);
                  const relative = timeAgo(scan.created_at);

                  return (
                    <tr key={scan.id}>
                      <td>
                        <div className="client-name-cell">
                          <div
                            className="client-avatar"
                            style={{
                              background: avatarColor,
                              minWidth: "28px",
                              width: "28px",
                              height: "28px",
                              fontSize: "11px",
                            }}
                          >
                            {initials(companyName)}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: "13px" }}>
                              {companyName}
                            </div>
                            {scan.multiple_locations && (
                              <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>
                                {scan.multiple_locations === "single" ? "Single location" : "Multiple locations"}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td>
                        {scan.website ? (
                          <a
                            href={
                              scan.website.startsWith("http://") ||
                              scan.website.startsWith("https://")
                                ? scan.website
                                : `https://${scan.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: "var(--accent-cyan)",
                              textDecoration: "none",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              fontSize: "12.5px",
                              wordBreak: "break-all",
                            }}
                          >
                            <span>{scan.website}</span>
                            <span style={{ fontSize: "11px" }}>↗</span>
                          </a>
                        ) : (
                          <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>—</span>
                        )}
                      </td>

                      <td>
                        <div style={{ fontSize: "12.5px", color: "var(--text-primary)" }}>
                          {formattedDate}
                        </div>
                        {relative && (
                          <div style={{ fontSize: "10.5px", color: "var(--text-muted)" }}>
                            {relative}
                          </div>
                        )}
                      </td>

                      <td>
                        <div
                          title={scan.description}
                          style={{
                            fontSize: "12.5px",
                            color: "var(--text-secondary)",
                            maxWidth: "320px",
                            lineHeight: "1.4",
                            overflow: "hidden",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          }}
                        >
                          {scan.description || (
                            <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>
                              No description provided
                            </span>
                          )}
                        </div>
                      </td>

                      <td>
                        {scan.business_type ? (
                          <span className="badge badge-purple" style={{ textTransform: "capitalize" }}>
                            {scan.business_type}
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-muted)", fontSize: "11px" }}>—</span>
                        )}
                      </td>

                      <td>
                        {scan.overall_score !== null && scan.overall_score !== undefined ? (
                          <span
                            className={`badge ${
                              scan.overall_score >= 70
                                ? "badge-green"
                                : scan.overall_score >= 40
                                ? "badge-orange"
                                : "badge-red"
                            }`}
                          >
                            {scan.overall_score}%
                          </span>
                        ) : (
                          <span style={{ color: "var(--text-muted)", fontSize: "11px" }}>—</span>
                        )}
                      </td>

                      <td style={{ textAlign: "right" }}>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "6px" }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => onSelectScan(scan)}
                            title="View Full Scan Details"
                            style={{ padding: "4px 8px", fontSize: "11px" }}
                          >
                            Details
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => onDeleteScan(scan)}
                            title="Delete Guest Scan"
                            style={{ padding: "4px 8px", fontSize: "11px", color: "var(--accent-red)" }}
                          >
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── TEAM TAB ────────────────────────────────────────────────────────────────
function TeamTab({ team, onCreateMember, onSaveMember, onDeleteMember }) {
  const [addingMember, setAddingMember] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [newMember, setNewMember] = useState({ name: "", role: "", color: "#7c5cfc" });

  const createMember = () => {
    if (!newMember.name || !newMember.role) {
      toast.warning("Please enter both Name and Role.");
      return;
    }
    onCreateMember(newMember);
    setAddingMember(false);
    setNewMember({ name: "", role: "", color: "#7c5cfc" });
  };

  const saveEdit = () => {
    if (!editingMember.name || !editingMember.role) {
      toast.warning("Please enter both Name and Role.");
      return;
    }
    onSaveMember(editingMember);
    setEditingMember(null);
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <div className="card-title">Team Members</div>
            <div className="card-sub">
              {team.length} members · tasks are assigned from the Task Board
            </div>
          </div>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setAddingMember(true)}
          >
            ＋ New Member
          </button>
        </div>
        <div className="scroll-x">
          <table className="table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Role</th>
                <th>Color Tag</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {team.map((m) => (
                <tr key={m.id}>
                  <td>
                    <div className="client-name-cell">
                      <div
                        className="task-assignee"
                        style={{
                          background: m.color,
                          width: 32,
                          height: 32,
                          fontSize: 12,
                        }}
                      >
                        {initials(m.name)}
                      </div>
                      <span style={{ fontWeight: 600 }}>{m.name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-purple">{m.role}</span>
                  </td>
                  <td>
                    <div
                      style={{
                        width: 16,
                        height: 16,
                        borderRadius: 4,
                        background: m.color,
                        display: "inline-block",
                      }}
                    />
                  </td>
                  <td>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditingMember(m)}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm btn-icon"
                        onClick={() => onDeleteMember(m.id)}
                      >
                        🗑
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Member Modal */}
      {addingMember && (
        <Modal
          title="Add New Team Member"
          onClose={() => setAddingMember(false)}
          footer={
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setAddingMember(false)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={createMember}>
                Add Member
              </button>
            </>
          }
        >
          <div className="input-group" style={{ marginBottom: 12 }}>
            <label className="input-label">Name</label>
            <input
              className="input"
              placeholder="e.g. Arjun Mehta"
              value={newMember.name}
              onChange={(e) =>
                setNewMember({ ...newMember, name: e.target.value })
              }
            />
          </div>
          <div className="input-group" style={{ marginBottom: 12 }}>
            <label className="input-label">Role</label>
            <input
              className="input"
              placeholder="e.g. Dev, SEO, Design"
              value={newMember.role}
              onChange={(e) =>
                setNewMember({ ...newMember, role: e.target.value })
              }
            />
          </div>
          <div className="input-group">
            <label className="input-label">Color Tag</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="color"
                className="input"
                style={{ padding: 0, width: 40, height: 36, cursor: "pointer", border: "none", background: "none" }}
                value={newMember.color}
                onChange={(e) =>
                  setNewMember({ ...newMember, color: e.target.value })
                }
              />
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {newMember.color}
              </span>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <Modal
          title={`Edit Member "${editingMember.name}"`}
          onClose={() => setEditingMember(null)}
          footer={
            <>
              <button
                className="btn btn-ghost"
                onClick={() => setEditingMember(null)}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveEdit}>
                Save Changes
              </button>
            </>
          }
        >
          <div className="input-group" style={{ marginBottom: 12 }}>
            <label className="input-label">Name</label>
            <input
              className="input"
              value={editingMember.name}
              onChange={(e) =>
                setEditingMember({ ...editingMember, name: e.target.value })
              }
            />
          </div>
          <div className="input-group" style={{ marginBottom: 12 }}>
            <label className="input-label">Role</label>
            <input
              className="input"
              value={editingMember.role}
              onChange={(e) =>
                setEditingMember({ ...editingMember, role: e.target.value })
              }
            />
          </div>
          <div className="input-group">
            <label className="input-label">Color Tag</label>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                type="color"
                className="input"
                style={{ padding: 0, width: 40, height: 36, cursor: "pointer", border: "none", background: "none" }}
                value={editingMember.color}
                onChange={(e) =>
                  setEditingMember({ ...editingMember, color: e.target.value })
                }
              />
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {editingMember.color}
              </span>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
const NAV = [
  { id: "overview", icon: "◈", label: "Overview" },
  { id: "plans", icon: "◇", label: "Plans & Pricing" },
  { id: "tasks", icon: "◻", label: "Task Board" },
  { id: "clients", icon: "◎", label: "Clients" },
  { id: "users", icon: "👥", label: "Users" },
  { id: "guest-scans", icon: "⚡", label: "Guest Scans" },
  { id: "team", icon: "◉", label: "Team" },
  { id: "support", icon: "✉", label: "Support Messages" },
];

export default function MasterAdmin() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [loadingAuth, setLoadingAuth] = useState(true);

  const handleLogout = async () => {
    try {
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((resolve) => setTimeout(resolve, 500)),
      ]);
    } catch (err) {
      console.warn("MasterAdmin: Error during signOut:", err);
    } finally {
      clearSupabaseAuthStorage();
      try {
        localStorage.clear();
      } catch (e) {
        // ignore localStorage clear error
      }
      window.location.href = "/auth";
    }
  };

  // Keep defaults as initial state so the page renders instantly without glitching
  const [plans, setPlans] = useState([]);
  const [team, setTeam] = useState([]);
  const [tasks, setTasks] = useState(TASK_DEFAULTS);
  const [clients, setClients] = useState(CLIENT_DEFAULTS);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [guestScans, setGuestScans] = useState([]);
  const [loadingGuestScans, setLoadingGuestScans] = useState(false);
  const [guestScansError, setGuestScansError] = useState(null);
  const [selectedGuestScanForDetails, setSelectedGuestScanForDetails] = useState(null);
  const [deleteConfirmGuestScan, setDeleteConfirmGuestScan] = useState(null);
  const [selectedClientForDetails, setSelectedClientForDetails] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(false);
  const [updatingPlan, setUpdatingPlan] = useState(false);
  const [updatingExtraScans, setUpdatingExtraScans] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  const [deleteConfirmUser, setDeleteConfirmUser] = useState(null);
  const [extraScansInput, setExtraScansInput] = useState(0);
  const [extraClientsInput, setExtraClientsInput] = useState(0);
  const [grantSlotsInput, setGrantSlotsInput] = useState(1);
  const [updatingExtraClients, setUpdatingExtraClients] = useState(false);
  const [agencyClientsList, setAgencyClientsList] = useState([]);
  const [loadingAgencyClients, setLoadingAgencyClients] = useState(false);
  const [agencyAllowanceInfo, setAgencyAllowanceInfo] = useState(null);
  const [newAgencyWebsite, setNewAgencyWebsite] = useState("");
  const [addingAgencyClient, setAddingAgencyClient] = useState(false);
  const [deletingAgencyClientWebsite, setDeletingAgencyClientWebsite] = useState(null);
  const [adminProfile, setAdminProfile] = useState(null);
  const [session, setSession] = useState(null);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  useEffect(() => {
    if (selectedClientForDetails) {
      setExtraScansInput(selectedClientForDetails.extra_scans || 0);
      setExtraClientsInput(selectedClientForDetails.extra_clients || 0);
      setGrantSlotsInput(1);
      setNewAgencyWebsite("");
      const planLow = (selectedClientForDetails.plan || "").toLowerCase();
      if (planLow === "agency") {
        fetchAgencyClientsForUser(selectedClientForDetails.id);
      } else {
        setAgencyClientsList([]);
        setAgencyAllowanceInfo(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientForDetails]);

  const allTasks = [
    ...(tasks.todo || []),
    ...(tasks.inprogress || []),
    ...(tasks.done || []),
  ];
  const tabTitle = NAV.find((n) => n.id === tab)?.label;

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const handleUpdateClientRole = async (clientId, isAdminValue) => {
    try {
      setUpdatingRole(true);
      const headers = await getHeaders();
      const payload = { is_admin: isAdminValue };
      await axios.put(`${API_URL}/admin/clients/${clientId}/role`, payload, headers);
      
      const newPlan = isAdminValue ? "Expansion" : "Guest";

      // Update local users state
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === clientId ? { ...u, is_admin: isAdminValue, plan: newPlan } : u))
      );
      
      // Update local clients state
      setClients((prevClients) =>
        prevClients.map((c) => (c.id === clientId ? { ...c, is_admin: isAdminValue, plan: newPlan } : c))
      );
      
      // Update selected client details state if open
      setSelectedClientForDetails((prevSelected) => {
        if (prevSelected && prevSelected.id === clientId) {
          return { ...prevSelected, is_admin: isAdminValue, plan: newPlan };
        }
        return prevSelected;
      });
    } catch (err) {
      console.error("Failed to update client role:", err);
      toast.error("Failed to update client role: " + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleUpdateUserPlan = async (userId, newPlanValue) => {
    try {
      setUpdatingPlan(true);
      const headers = await getHeaders();
      const payload = { plan: newPlanValue };
      await axios.put(`${API_URL}/admin/users/${userId}/plan`, payload, headers);
      
      const planLabel = capitalizePlan(newPlanValue);

      // Update local users state
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === userId ? { ...u, plan: planLabel } : u))
      );
      
      // Update local clients state
      setClients((prevClients) =>
        prevClients.map((c) => (c.id === userId ? { ...c, plan: planLabel } : c))
      );
      
      // Update selected client details state if open
      setSelectedClientForDetails((prevSelected) => {
        if (prevSelected && prevSelected.id === userId) {
          return { ...prevSelected, plan: planLabel };
        }
        return prevSelected;
      });

      if (newPlanValue.toLowerCase() === "agency") {
        fetchAgencyClientsForUser(userId);
      } else {
        setAgencyClientsList([]);
        setAgencyAllowanceInfo(null);
      }

      toast.success("User plan updated successfully!");
    } catch (err) {
      console.error("Failed to update user plan:", err);
      toast.error("Failed to update user plan: " + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingPlan(false);
    }
  };

  const fetchAgencyClientsForUser = async (userId) => {
    try {
      setLoadingAgencyClients(true);
      const headers = await getHeaders();
      const res = await axios.get(`${API_URL}/admin/users/${userId}/agency-clients`, headers);
      setAgencyClientsList(res.data.clients || []);
      setAgencyAllowanceInfo({
        total_clients: res.data.total_clients,
        extra_clients: res.data.extra_clients,
        complimentary_limit: res.data.complimentary_limit,
        max_limit: res.data.max_limit,
      });
      setExtraClientsInput(res.data.extra_clients || 0);
    } catch (err) {
      console.error("Failed to fetch user agency clients:", err);
    } finally {
      setLoadingAgencyClients(false);
    }
  };

  const handleUpdateUserExtraClients = async (userId, newExtraClients) => {
    try {
      setUpdatingExtraClients(true);
      const headers = await getHeaders();
      const payload = { extra_clients: newExtraClients };
      await axios.put(`${API_URL}/admin/users/${userId}/extra-clients`, payload, headers);
      
      // Update local users state
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === userId ? { ...u, extra_clients: newExtraClients } : u))
      );
      
      // Update local clients state
      setClients((prevClients) =>
        prevClients.map((c) => (c.id === userId ? { ...c, extra_clients: newExtraClients } : c))
      );
      
      // Update selected client details state if open
      setSelectedClientForDetails((prevSelected) => {
        if (prevSelected && prevSelected.id === userId) {
          return { ...prevSelected, extra_clients: newExtraClients };
        }
        return prevSelected;
      });
      
      if (agencyAllowanceInfo) {
        setAgencyAllowanceInfo((prev) => ({
          ...prev,
          extra_clients: newExtraClients,
          complimentary_limit: 1 + newExtraClients,
          max_limit: 20 + newExtraClients,
        }));
      }
      setExtraClientsInput(newExtraClients);
      toast.success(`Agency extra clients allowance updated successfully! (${1 + newExtraClients} complimentary client websites without paying)`);
    } catch (err) {
      console.error("Failed to update extra clients:", err);
      toast.error("Failed to update extra clients: " + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingExtraClients(false);
    }
  };

  const handleGrantAdditionalFreeSlots = async (userId, countToAdd) => {
    const addCount = parseInt(countToAdd, 10);
    if (isNaN(addCount) || addCount <= 0) {
      toast.error("Please specify a valid number of free websites to grant.");
      return;
    }

    const currentAssigned = agencyClientsList.length;
    const currentExtra = selectedClientForDetails?.extra_clients || 0;
    const currentQuota = 1 + currentExtra;
    
    // Grant addCount slots on top of either current quota or currently assigned count (whichever is higher)
    const baseForGrant = Math.max(currentAssigned, currentQuota);
    const newQuota = baseForGrant + addCount;
    const newExtraClients = Math.max(0, newQuota - 1);

    await handleUpdateUserExtraClients(userId, newExtraClients);
    const remainingNow = newQuota - currentAssigned;
    toast.success(`Granted ${addCount} extra free website slot(s)! User now has ${remainingNow} free slot(s) remaining to add.`);
  };

  const handleAddAgencyClientDirect = async (userId) => {
    const web = newAgencyWebsite.trim();
    if (!web) {
      toast.error("Please enter a website URL.");
      return;
    }
    try {
      setAddingAgencyClient(true);
      const headers = await getHeaders();
      const res = await axios.post(`${API_URL}/admin/users/${userId}/agency-clients`, { website: web }, headers);
      setAgencyClientsList((prev) => [...prev, res.data]);
      setNewAgencyWebsite("");
      if (agencyAllowanceInfo) {
        setAgencyAllowanceInfo((prev) => ({
          ...prev,
          total_clients: (prev.total_clients || 0) + 1,
        }));
      }
      toast.success(`Client website '${res.data.website}' successfully added to agency!`);
    } catch (err) {
      console.error("Failed to add agency client:", err);
      toast.error(err.response?.data?.detail || "Failed to add client website.");
    } finally {
      setAddingAgencyClient(false);
    }
  };

  const handleDeleteAgencyClientDirect = async (userId, website) => {
    try {
      setDeletingAgencyClientWebsite(website);
      const headers = await getHeaders();
      await axios.delete(`${API_URL}/admin/users/${userId}/agency-clients?website=${encodeURIComponent(website)}`, headers);
      setAgencyClientsList((prev) => prev.filter((c) => c.website !== website));
      if (agencyAllowanceInfo) {
        setAgencyAllowanceInfo((prev) => ({
          ...prev,
          total_clients: Math.max(0, (prev.total_clients || 1) - 1),
        }));
      }
      toast.success(`Client website '${website}' removed from agency.`);
    } catch (err) {
      console.error("Failed to delete agency client:", err);
      toast.error(err.response?.data?.detail || "Failed to remove client website.");
    } finally {
      setDeletingAgencyClientWebsite(null);
    }
  };

  const handleUpdateUserExtraScans = async (userId, newExtraScans) => {
    try {
      setUpdatingExtraScans(true);
      const headers = await getHeaders();
      const payload = { extra_scans: newExtraScans };
      await axios.put(`${API_URL}/admin/users/${userId}/extra-scans`, payload, headers);
      
      // Update local users state
      setUsers((prevUsers) =>
        prevUsers.map((u) => (u.id === userId ? { ...u, extra_scans: newExtraScans } : u))
      );
      
      // Update local clients state
      setClients((prevClients) =>
        prevClients.map((c) => (c.id === userId ? { ...c, extra_scans: newExtraScans } : c))
      );
      
      // Update selected client details state if open
      setSelectedClientForDetails((prevSelected) => {
        if (prevSelected && prevSelected.id === userId) {
          return { ...prevSelected, extra_scans: newExtraScans };
        }
        return prevSelected;
      });
      toast.success("User extra scans updated successfully!");
    } catch (err) {
      console.error("Failed to update user extra scans:", err);
      toast.error("Failed to update user extra scans: " + (err.response?.data?.detail || err.message));
    } finally {
      setUpdatingExtraScans(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setDeletingUser(true);
      const headers = await getHeaders();
      await axios.delete(`${API_URL}/admin/users/${userId}`, headers);
      
      // Update local users state
      setUsers((prevUsers) => prevUsers.filter((u) => u.id !== userId));
      
      // Update local clients state
      setClients((prevClients) => prevClients.filter((c) => c.id !== userId));
      
      // Close sidebar details
      setSelectedClientForDetails(null);
      setDeleteConfirmUser(null);
      toast.success("User deleted successfully!");
    } catch (err) {
      console.error("Failed to delete user:", err);
      toast.error("Failed to delete user: " + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingUser(false);
    }
  };

  const capitalizePlan = (p) => {
    if (!p) return "Guest";
    const low = p.toLowerCase();
    if (low === "core") return "Core";
    if (low === "growth") return "Growth";
    if (low === "expansion") return "Expansion";
    if (low === "agency") return "Agency";
    return p.charAt(0).toUpperCase() + p.slice(1);
  };

  const getPlanLimitValue = (planName) => {
    const low = (planName || "guest").toLowerCase();
    return { core: 1, growth: 20, expansion: 999999, guest: 1, agency: 999999 }[low] || 0;
  };

  const getHeaders = async () => {
    // 1. Try component state session first
    if (session && session.access_token) {
      return {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      };
    }

    // 2. Try getSafeSession with a 10-second timeout
    const result = await getSafeSession(10000);
    const activeSession = result.data?.session;
    if (activeSession && activeSession.access_token) {
      return {
        headers: {
          Authorization: `Bearer ${activeSession.access_token}`,
        },
      };
    }

    // 3. Try LocalStorage fallback directly
    try {
      const storageKey = Object.keys(localStorage).find(
        (key) => key.startsWith("sb-") && key.endsWith("-auth-token")
      );
      if (storageKey) {
        const localData = JSON.parse(localStorage.getItem(storageKey));
        if (localData && localData.access_token) {
          return {
            headers: {
              Authorization: `Bearer ${localData.access_token}`,
            },
          };
        }
      }
    } catch (e) {
      console.error("MasterAdmin getHeaders localStorage fallback error:", e);
    }

    return {};
  };

  const loadData = async () => {
    try {
      const headers = await getHeaders();
      if (!headers.headers) {
        console.warn("No active session found during fetch attempt.");
        return;
      }

      const fetchSafe = async (url) => {
        try {
          const res = await axios.get(url, headers);
          return res.data;
        } catch (err) {
          console.error(`Failed to fetch from ${url}:`, err);
          return null;
        }
      };

      const [plansData, teamData, tasksData, clientsData, profileData, guestScansData] = await Promise.all([
        fetchSafe(`${API_URL}/admin/plans`),
        fetchSafe(`${API_URL}/admin/team`),
        fetchSafe(`${API_URL}/admin/tasks`),
        fetchSafe(`${API_URL}/admin/clients`),
        fetchSafe(`${API_URL}/user/profile`),
        fetchSafe(`${API_URL}/admin/guest-scans`),
      ]);

      if (plansData) setPlans(plansData);
      if (teamData) setTeam(teamData);
      if (tasksData) setTasks(tasksData);
      if (clientsData) setClients(clientsData);
      if (profileData) setAdminProfile(profileData);
      if (guestScansData && guestScansData.scans) setGuestScans(guestScansData.scans);
    } catch (error) {
      console.error("Failed to load admin dashboard dynamic data:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      setUsersError(null);
      const headers = await getHeaders();
      const res = await axios.get(`${API_URL}/admin/users`, headers);
      setUsers(res.data);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setUsersError("Failed to load users list. Please try again.");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchGuestScans = async () => {
    try {
      setLoadingGuestScans(true);
      setGuestScansError(null);
      const headers = await getHeaders();
      const res = await axios.get(`${API_URL}/admin/guest-scans`, headers);
      if (res.data && res.data.scans) {
        setGuestScans(res.data.scans);
      }
    } catch (err) {
      console.error("Failed to fetch guest scans:", err);
      setGuestScansError("Failed to load guest scans. Please try again.");
    } finally {
      setLoadingGuestScans(false);
    }
  };

  const handleDeleteGuestScan = async (scanId) => {
    try {
      const headers = await getHeaders();
      await axios.delete(`${API_URL}/admin/guest-scans/${scanId}`, headers);
      setGuestScans((prev) => prev.filter((s) => s.id !== scanId));
      toast.success("Guest scan deleted successfully");
      setDeleteConfirmGuestScan(null);
      if (selectedGuestScanForDetails?.id === scanId) {
        setSelectedGuestScanForDetails(null);
      }
    } catch (err) {
      console.error("Failed to delete guest scan:", err);
      toast.error("Failed to delete guest scan record");
    }
  };

  useEffect(() => {
    if (tab === "users" && users.length === 0) {
      fetchUsers();
    }
    if (tab === "guest-scans" && guestScans.length === 0) {
      fetchGuestScans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ─── PLANS CALLBACK HANDLERS ────────────────────────────────────────────────
  const handleSavePlan = async (editingPlan) => {
    try {
      const headers = await getHeaders();
      const updatedPlan = {
        name: editingPlan.name,
        price: Number(editingPlan.price),
        popular: editingPlan.popular,
        features: editingPlan.features,
        limits: editingPlan.limits || { clients: 1, reports: 1, platforms: 5 },
      };
      const res = await axios.put(`${API_URL}/admin/plans/${editingPlan.id}`, updatedPlan, headers);
      setPlans(plans.map((p) => (p.id === editingPlan.id ? res.data : p)));
    } catch (err) {
      console.error("Failed to save plan:", err);
    }
  };

  const handleDeletePlan = async (id) => {
    try {
      const headers = await getHeaders();
      await axios.delete(`${API_URL}/admin/plans/${id}`, headers);
      setPlans(plans.filter((p) => p.id !== id));
    } catch (err) {
      console.error("Failed to delete plan:", err);
    }
  };

  const handleCreatePlan = async (newPlan) => {
    try {
      const headers = await getHeaders();
      const planPayload = {
        name: newPlan.name,
        price: Number(newPlan.price),
        popular: false,
        features: newPlan.features,
        limits: { clients: 1, reports: 1, platforms: 5 },
      };
      const res = await axios.post(`${API_URL}/admin/plans`, planPayload, headers);
      setPlans([...plans, res.data]);
    } catch (err) {
      console.error("Failed to create plan:", err);
    }
  };

  // ─── TASKS CALLBACK HANDLERS ────────────────────────────────────────────────
  const handleAddTask = async (newTask) => {
    try {
      const headers = await getHeaders();
      const payload = {
        title: newTask.title,
        client: newTask.client,
        assignee: newTask.assignee ? Number(newTask.assignee) : null,
        priority: newTask.priority,
        status: newTask.status,
      };
      const res = await axios.post(`${API_URL}/admin/tasks`, payload, headers);
      setTasks((prev) => ({
        ...prev,
        [res.data.status]: [...(prev[res.data.status] || []), res.data],
      }));
    } catch (err) {
      console.error("Failed to add task:", err);
      toast.error("Failed to add task: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleMoveTask = async (task, from, to) => {
    try {
      const headers = await getHeaders();
      const payload = { status: to };
      const res = await axios.put(`${API_URL}/admin/tasks/${task.id}`, payload, headers);
      setTasks((prev) => ({
        ...prev,
        [from]: (prev[from] || []).filter((t) => t.id !== task.id),
        [to]: [...(prev[to] || []), res.data],
      }));
    } catch (err) {
      console.error("Failed to move task:", err);
      toast.error("Failed to move task: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleReassignTask = async (task, col, memberId) => {
    try {
      const headers = await getHeaders();
      const payload = { assignee: memberId };
      const res = await axios.put(`${API_URL}/admin/tasks/${task.id}`, payload, headers);
      setTasks((prev) => ({
        ...prev,
        [col]: (prev[col] || []).map((t) => (t.id === task.id ? res.data : t)),
      }));
    } catch (err) {
      console.error("Failed to reassign task:", err);
      toast.error("Failed to reassign task: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteTask = async (id, col) => {
    try {
      const headers = await getHeaders();
      await axios.delete(`${API_URL}/admin/tasks/${id}`, headers);
      setTasks((prev) => ({
        ...prev,
        [col]: (prev[col] || []).filter((t) => t.id !== id),
      }));
    } catch (err) {
      console.error("Failed to delete task:", err);
      toast.error("Failed to delete task: " + (err.response?.data?.detail || err.message));
    }
  };

  // ─── CLIENTS CALLBACK HANDLERS ──────────────────────────────────────────────
  const handleAddClient = async (newClient) => {
    try {
      const headers = await getHeaders();
      const payload = {
        name: newClient.name,
        plan: newClient.plan,
        status: newClient.status,
      };
      const res = await axios.post(`${API_URL}/admin/clients`, payload, headers);
      setClients([...clients, res.data]);
    } catch (err) {
      console.error("Failed to add client:", err);
    }
  };

  const handleAddClientTask = async (clientName, clientTask) => {
    try {
      const headers = await getHeaders();
      const payload = {
        title: clientTask.title,
        client: clientName,
        assignee: clientTask.assignee ? Number(clientTask.assignee) : null,
        priority: clientTask.priority,
        status: "todo",
      };
      const res = await axios.post(`${API_URL}/admin/tasks`, payload, headers);
      
      setTasks((prev) => ({
        ...prev,
        todo: [...(prev.todo || []), res.data],
      }));
      
      setClients(clients.map((c) => (c.name === clientName ? { ...c, tasks: c.tasks + 1 } : c)));
    } catch (err) {
      console.error("Failed to add client task:", err);
    }
  };

  // ─── TEAM CALLBACK HANDLERS ──────────────────────────────────────────────────
  const handleCreateTeamMember = async (newMember) => {
    try {
      const headers = await getHeaders();
      const payload = {
        name: newMember.name,
        role: newMember.role,
        color: newMember.color,
      };
      const res = await axios.post(`${API_URL}/admin/team`, payload, headers);
      setTeam([...team, res.data]);
    } catch (err) {
      console.error("Failed to create team member:", err);
      toast.error("Failed to create team member: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveTeamMember = async (editingMember) => {
    try {
      const headers = await getHeaders();
      const payload = {
        name: editingMember.name,
        role: editingMember.role,
        color: editingMember.color,
      };
      const res = await axios.put(`${API_URL}/admin/team/${editingMember.id}`, payload, headers);
      setTeam(team.map((m) => (m.id === editingMember.id ? res.data : m)));
    } catch (err) {
      console.error("Failed to save team member:", err);
      toast.error("Failed to save team member: " + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteTeamMember = async (id) => {
    try {
      const headers = await getHeaders();
      await axios.delete(`${API_URL}/admin/team/${id}`, headers);
      setTeam(team.filter((m) => m.id !== id));
    } catch (err) {
      console.error("Failed to delete team member:", err);
      toast.error("Failed to delete team member: " + (err.response?.data?.detail || err.message));
    }
  };

  // ─── DYNAMIC FETCHING EFFECT ────────────────────────────────────────────────
  useEffect(() => {
    const checkSessionAndLoad = async () => {
      try {
        let session = getCachedSession();
        const isCachedExpired = session?.expires_at
          ? session.expires_at < Math.floor(Date.now() / 1000)
          : true;

        let sessionError = null;

        // Only perform network getSafeSession if we don't have a cached session or it is expired
        if (!session || isCachedExpired) {
          try {
            const result = await getSafeSession(15000);
            if (result.data?.session) {
              session = result.data.session;
            }
            sessionError = result.error;
          } catch (err) {
            sessionError = err;
          }
        }

        if (!session) {
          const storageKey = Object.keys(localStorage).find(key => key.startsWith("sb-") && key.endsWith("-auth-token"));
          if (!storageKey) {
            navigate("/auth");
          } else {
            console.warn("MasterAdmin: getSafeSession timed out/failed, and fallback failed.");
          }
          return;
        }

        const isExpired = session.expires_at ? (session.expires_at < Math.floor(Date.now() / 1000)) : true;
        if (sessionError && isExpired) {
          console.warn("MasterAdmin: Session is expired and token refresh timed out. Redirecting to /auth.");
          clearSupabaseAuthStorage();
          navigate("/auth");
          return;
        }

        if (session?.access_token && session.user) {
          setSession(session);
          try {
            const res = await axios.get(`${API_URL}/admin/plans`, {
              headers: {
                Authorization: `Bearer ${session.access_token}`,
              },
            });
            if (res.data) setPlans(res.data);
            loadData();
            setLoadingAuth(false);
          } catch (err) {
            console.warn("MasterAdmin: Unauthorized admin access. Redirecting to /user-dashboard", err);
            navigate("/user-dashboard");
          }
        }
      } catch (err) {
        console.error("MasterAdmin: checkSessionAndLoad exception:", err);
        navigate("/user-dashboard");
      }
    };
    checkSessionAndLoad();

    const {
      data: { subscription: authListener },
    } = supabase.auth.onAuthStateChange((event, session) => {
      setTimeout(async () => {
        try {
          if (event === "SIGNED_IN" || (event === "INITIAL_SESSION" && session)) {
            if (session?.access_token && session.user) {
              setSession(session);
              try {
                const res = await axios.get(`${API_URL}/admin/plans`, {
                  headers: {
                    Authorization: `Bearer ${session.access_token}`,
                  },
                });
                if (res.data) setPlans(res.data);
                loadData();
                setLoadingAuth(false);
              } catch (err) {
                console.warn("MasterAdmin: Unauthorized admin on auth change. Redirecting to /user-dashboard", err);
                navigate("/user-dashboard");
              }
            }
          } else if (event === "TOKEN_REFRESHED" && session) {
            // Update session state without calling backend admin APIs to prevent deadlocks
            setSession(session);
          } else if (event === "SIGNED_OUT") {
            navigate("/auth");
          }
        } catch (err) {
          console.error("MasterAdmin: onAuthStateChange exception:", err);
          navigate("/user-dashboard");
        }
      }, 0);
    });

    return () => {
      authListener?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingAuth) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        background: "var(--bg-primary)",
        color: "var(--text-secondary)",
        fontFamily: "Inter, sans-serif"
      }}>
        Verifying authorization...
      </div>
    );
  }

  const adminName = adminProfile?.company_name || session?.user?.email?.split("@")[0] || "Master Admin";
  const adminInitials = initials(adminName);

  return (
    <>
      <div className="admin-shell">
        {/* SIDEBAR */}
        <aside className="sidebar">
          <div className="sidebar-logo">
            <div className="logo-dot">S</div>
            <div>
              <div className="logo-text">SeenByAI</div>
              <div className="logo-badge">MASTER ADMIN</div>
            </div>
          </div>
          <nav className="sidebar-nav">
            <div className="nav-section-label">Dashboard</div>
            {NAV.map((n) => (
              <button
                key={n.id}
                className={`nav-item${tab === n.id ? " active" : ""}`}
                onClick={() => setTab(n.id)}
              >
                <span className="nav-icon">{n.icon}</span>
                <span style={{ flex: 1, textAlign: "left" }}>{n.label}</span>
                {n.id === "guest-scans" && guestScans.length > 0 && (
                  <span
                    className="badge badge-orange"
                    style={{ fontSize: "10px", padding: "2px 7px" }}
                  >
                    {guestScans.length}
                  </span>
                )}
              </button>
            ))}
          </nav>
          <div className="sidebar-footer" style={{ position: "relative" }}>
            <div 
              className="sidebar-footer-trigger" 
              onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
              style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", cursor: "pointer" }}
            >
              <div className="avatar">{adminInitials}</div>
              <div className="avatar-info">
                <div className="avatar-name">{adminName}</div>
                <div className="avatar-role">Master Admin</div>
              </div>
              <div className="user-chevron-arrow" style={{ color: "var(--text-muted)", marginLeft: "auto" }}>
                <FiChevronRight
                  style={{
                    transform: profileDropdownOpen ? "rotate(-90deg)" : "rotate(90deg)",
                    transition: "transform 0.2s ease",
                  }}
                  size={14}
                />
              </div>
            </div>

            {/* Dropup Actions Menu */}
            {profileDropdownOpen && (
              <>
                <div
                  className="user-dropdown-backdrop"
                  onClick={() => setProfileDropdownOpen(false)}
                />
                <div className="admin-profile-dropup-menu">
                  <button
                    className="admin-dropdown-item"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      navigate("/user-dashboard");
                    }}
                  >
                    <span className="nav-icon"><FiArrowLeft size={14} /></span>
                    User Dashboard
                  </button>
                  <div className="admin-dropdown-divider" />
                  <button
                    className="admin-dropdown-item logout-btn"
                    onClick={() => {
                      setProfileDropdownOpen(false);
                      handleLogout();
                    }}
                  >
                    <span className="nav-icon"><FiLogOut size={14} /></span>
                    Log Out
                  </button>
                </div>
              </>
            )}
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main className="main">
          <div className="topbar">
            <div>
              <div className="topbar-title">{tabTitle}</div>
              <div className="topbar-sub">SeenByAI · Admin Console</div>
            </div>
            <div className="topbar-actions">
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
              </span>
              <div
                className="avatar"
                style={{ width: 32, height: 32, fontSize: 12 }}
              >
                {adminInitials}
              </div>
            </div>
          </div>

          <div className="content">
            {/* OVERVIEW */}
            {tab === "overview" && (
              <>
                <div className="stats-row">
                  <div className="stat-card cyan">
                    <div className="stat-icon cyan">👥</div>
                    <div className="stat-value">{clients.length}</div>
                    <div className="stat-label">Total Clients</div>
                  </div>
                  <div className="stat-card purple">
                    <div className="stat-icon purple">◻</div>
                    <div className="stat-value">{allTasks.length}</div>
                    <div className="stat-label">Active Tasks</div>
                  </div>
                  <div className="stat-card green">
                    <div className="stat-icon green">◇</div>
                    <div className="stat-value">{plans.length}</div>
                    <div className="stat-label">Active Plans</div>
                  </div>
                  <div
                    className="stat-card orange"
                    style={{ cursor: "pointer" }}
                    onClick={() => setTab("guest-scans")}
                    title="Click to view all guest scans"
                  >
                    <div className="stat-icon orange">⚡</div>
                    <div className="stat-value">{guestScans.length}</div>
                    <div className="stat-label">Guest Scans</div>
                    <div className="stat-change up" style={{ fontSize: 11, marginTop: 2 }}>
                      Unregistered Leads ↗
                    </div>
                  </div>
                </div>

                <div className="section-row">
                  <div className="card">
                    <div className="card-header">
                      <div className="card-title">Recent Clients</div>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setTab("clients")}
                      >
                        View all
                      </button>
                    </div>
                    <div style={{ padding: "0 0 8px" }}>
                      {clients.slice(0, 4).map((c) => (
                        <div
                          key={c.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "12px 22px",
                            borderBottom: "1px solid var(--border)",
                          }}
                        >
                          <div
                            className="client-avatar"
                            style={{ background: c.color }}
                          >
                            {initials(c.name)}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 13, fontWeight: 600 }}>
                              {c.name}
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: "var(--text-muted)",
                              }}
                            >
                              {c.plan} · {c.joined}
                            </div>
                          </div>
                          <span
                            className={`badge ${statusBadge[c.status] || "badge-neutral"}`}
                          >
                            {c.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-header">
                      <div className="card-title">Task Pipeline</div>
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => setTab("tasks")}
                      >
                        View all
                      </button>
                    </div>
                    <div
                      style={{
                        padding: "14px 22px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      {[
                        [
                          "To Do",
                          (tasks.todo || []).length,
                          "var(--text-muted)",
                        ],
                        [
                          "In Progress",
                          (tasks.inprogress || []).length,
                          "var(--accent-orange)",
                        ],
                        [
                          "Done",
                          (tasks.done || []).length,
                          "var(--accent-green)",
                        ],
                      ].map(([label, count, color]) => (
                        <div
                          key={label}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              width: 80,
                              fontSize: 12,
                              color: "var(--text-secondary)",
                            }}
                          >
                            {label}
                          </div>
                          <div
                            style={{
                              flex: 1,
                              height: 6,
                              background: "var(--bg-elevated)",
                              borderRadius: 99,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              style={{
                                height: "100%",
                                width: `${allTasks.length > 0 ? (count / allTasks.length) * 100 : 0}%`,
                                background: color,
                                borderRadius: 99,
                              }}
                            />
                          </div>
                          <div
                            style={{
                              width: 20,
                              fontSize: 13,
                              fontWeight: 700,
                              color,
                              textAlign: "right",
                            }}
                          >
                            {count}
                          </div>
                        </div>
                      ))}
                      <div style={{ marginTop: 8 }}>
                        <div
                          style={{
                            fontSize: 11,
                            color: "var(--text-muted)",
                            marginBottom: 8,
                          }}
                        >
                          Team
                        </div>
                        <div
                          style={{ display: "flex", gap: 6, flexWrap: "wrap" }}
                        >
                          {team.map((m) => (
                            <div
                              key={m.id}
                              className="task-assignee"
                              style={{
                                background: m.color,
                                width: 30,
                                height: 30,
                                fontSize: 11,
                              }}
                              title={m.name}
                            >
                              {initials(m.name)}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* RECENT GUEST SCANNERS ON OVERVIEW */}
                <div className="card" style={{ marginTop: 20 }}>
                  <div className="card-header">
                    <div>
                      <div className="card-title">Recent Guest Scanners</div>
                      <div className="card-sub">
                        Non-logged in visitors who ran scans ({guestScans.length} total)
                      </div>
                    </div>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setTab("guest-scans")}
                    >
                      View all ({guestScans.length})
                    </button>
                  </div>
                  <div className="scroll-x">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Company</th>
                          <th>Website</th>
                          <th>Date</th>
                          <th>Description</th>
                          <th>AI Score</th>
                          <th style={{ textAlign: "right" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {guestScans.length === 0 ? (
                          <tr>
                            <td colSpan="6" style={{ textAlign: "center", padding: "28px", color: "var(--text-secondary)" }}>
                              No guest scans recorded yet.
                            </td>
                          </tr>
                        ) : (
                          guestScans.slice(0, 5).map((s) => (
                            <tr key={s.id}>
                              <td>
                                <div className="client-name-cell">
                                  <div
                                    className="client-avatar"
                                    style={{
                                      background: getCompanyColor(s.company),
                                      width: 26,
                                      height: 26,
                                      fontSize: 10,
                                      minWidth: 26,
                                    }}
                                  >
                                    {initials(s.company || "Unknown")}
                                  </div>
                                  <span style={{ fontWeight: 600, fontSize: 13 }}>{s.company || "Unknown"}</span>
                                </div>
                              </td>
                              <td>
                                <a
                                  href={s.website && (s.website.startsWith("http://") || s.website.startsWith("https://")) ? s.website : `https://${s.website}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ color: "var(--accent-cyan)", fontSize: 12.5, textDecoration: "none" }}
                                >
                                  {s.website} ↗
                                </a>
                              </td>
                              <td>
                                <span style={{ fontSize: 12, color: "var(--text-primary)" }}>
                                  {formatScanDate(s.created_at)}
                                </span>
                              </td>
                              <td>
                                <span
                                  style={{
                                    fontSize: 12,
                                    color: "var(--text-secondary)",
                                    maxWidth: 240,
                                    display: "inline-block",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                  title={s.description}
                                >
                                  {s.description || "—"}
                                </span>
                              </td>
                              <td>
                                {s.overall_score !== null && s.overall_score !== undefined ? (
                                  <span
                                    className={`badge ${
                                      s.overall_score >= 70
                                        ? "badge-green"
                                        : s.overall_score >= 40
                                        ? "badge-orange"
                                        : "badge-red"
                                    }`}
                                  >
                                    {s.overall_score}%
                                  </span>
                                ) : (
                                  <span style={{ color: "var(--text-muted)", fontSize: 11 }}>—</span>
                                )}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                <button
                                  className="btn btn-ghost btn-sm"
                                  onClick={() => setSelectedGuestScanForDetails(s)}
                                  style={{ padding: "3px 8px", fontSize: 11 }}
                                >
                                  Details
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            {tab === "plans" && (
              <PlansTab
                plans={plans}
                onSavePlan={handleSavePlan}
                onDeletePlan={handleDeletePlan}
                onCreatePlan={handleCreatePlan}
              />
            )}
            {tab === "tasks" && (
              <TasksTab
                clients={clients}
                tasks={tasks}
                team={team}
                onAddTask={handleAddTask}
                onMoveTask={handleMoveTask}
                onReassignTask={handleReassignTask}
                onDeleteTask={handleDeleteTask}
              />
            )}
            {tab === "clients" && (
              <ClientsTab
                clients={clients}
                onAddClient={handleAddClient}
                team={team}
                onAddClientTask={handleAddClientTask}
                allTasks={allTasks}
                plans={plans}
                onSelectClient={setSelectedClientForDetails}
              />
            )}
            {tab === "users" && (
              <UsersTab
                users={users}
                onSelectClient={setSelectedClientForDetails}
                loading={loadingUsers}
                error={usersError}
                onRefresh={fetchUsers}
              />
            )}
            {tab === "guest-scans" && (
              <GuestScansTab
                guestScans={guestScans}
                loading={loadingGuestScans}
                error={guestScansError}
                onRefresh={fetchGuestScans}
                onSelectScan={setSelectedGuestScanForDetails}
                onDeleteScan={setDeleteConfirmGuestScan}
              />
            )}
            {tab === "team" && (
              <TeamTab
                team={team}
                onCreateMember={handleCreateTeamMember}
                onSaveMember={handleSaveTeamMember}
                onDeleteMember={handleDeleteTeamMember}
              />
            )}
            {tab === "support" && (
              <SupportMessagesAdmin />
            )}
          </div>
        </main>
      </div>

      {selectedClientForDetails && (
        <div className="details-sidebar-overlay" onClick={() => setSelectedClientForDetails(null)}>
          <div className="details-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="details-sidebar-header">
              <span className="details-sidebar-title">Client Details</span>
              <button className="btn btn-ghost btn-sm" onClick={() => setSelectedClientForDetails(null)}>✕</button>
            </div>
            <div className="details-sidebar-body">
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div className="client-avatar" style={{ background: selectedClientForDetails.color, width: 64, height: 64, fontSize: 24 }}>
                  {initials(selectedClientForDetails.name)}
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, textAlign: "center" }}>{selectedClientForDetails.name}</div>
                <span className={`badge ${selectedClientForDetails.is_admin ? "badge-purple" : "badge-neutral"}`}>
                  {selectedClientForDetails.is_admin ? "Admin Account" : "User Account"}
                </span>
              </div>

              <div className="details-card">
                <div className="details-item">
                  <div className="details-label">Email Address</div>
                  <div className="details-value">{selectedClientForDetails.email || "No email info"}</div>
                </div>
                <div className="details-item">
                  <div className="details-label">Current Plan</div>
                  <select
                    className="input"
                    value={(selectedClientForDetails.plan || "Guest").toLowerCase()}
                    onChange={(e) => handleUpdateUserPlan(selectedClientForDetails.id, e.target.value)}
                    disabled={updatingPlan}
                    style={{ width: "100%", marginTop: "6px" }}
                  >
                    <option value="guest">Guest</option>
                    <option value="core">Core</option>
                    <option value="growth">Growth</option>
                    <option value="expansion">Expansion</option>
                    <option value="agency">Agency</option>
                  </select>
                </div>
                <div className="details-item" style={{ marginTop: "12px" }}>
                  <div className="details-label">Extra Scans</div>
                  <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "6px" }}>
                    <input
                      className="input"
                      type="number"
                      min="0"
                      value={extraScansInput}
                      onChange={(e) => setExtraScansInput(parseInt(e.target.value) || 0)}
                      disabled={updatingExtraScans}
                      style={{ width: "80px" }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      style={{ padding: "6px 12px", height: "34px", fontSize: "12px" }}
                      onClick={() => handleUpdateUserExtraScans(selectedClientForDetails.id, extraScansInput)}
                      disabled={updatingExtraScans || extraScansInput === (selectedClientForDetails.extra_scans || 0)}
                    >
                      Update
                    </button>
                  </div>
                  <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginTop: "6px" }}>
                    Total scan limit: {
                      (selectedClientForDetails.plan?.toLowerCase() === "expansion" || selectedClientForDetails.plan?.toLowerCase() === "agency" || selectedClientForDetails.is_admin)
                        ? "Unlimited"
                        : `${getPlanLimitValue(selectedClientForDetails.plan) + (selectedClientForDetails.extra_scans || 0)} (Used: ${selectedClientForDetails.scans_used || 0})`
                    }
                  </span>
                </div>
                <div className="details-item">
                  <div className="details-label">Status</div>
                  <div className="details-value">{selectedClientForDetails.status}</div>
                </div>
                <div className="details-item">
                  <div className="details-label">Joined</div>
                  <div className="details-value">{selectedClientForDetails.joined}</div>
                </div>
              </div>

              {(selectedClientForDetails.plan || "").toLowerCase() === "agency" && (
                <div className="details-card agency-details-card" style={{ marginTop: "16px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <div style={{ fontWeight: 700, fontSize: "14px", color: "var(--accent-cyan)", display: "flex", alignItems: "center", gap: "6px" }}>
                      <span>🏢</span> Agency Client Websites
                    </div>
                    {agencyAllowanceInfo && (
                      <span className="badge badge-cyan" style={{ fontSize: "11px" }}>
                        {agencyClientsList.length} / {agencyAllowanceInfo.max_limit}
                      </span>
                    )}
                  </div>

                  {/* Complimentary Allowance Summary & Quick Grant */}
                  {(() => {
                    const currentExtraClients = selectedClientForDetails.extra_clients || 0;
                    const complimentaryLimit = 1 + currentExtraClients;
                    const currentAssigned = agencyClientsList.length;
                    const remainingFreeSlots = Math.max(0, complimentaryLimit - currentAssigned);

                    return (
                      <>
                        <div
                          style={{
                            background: "rgba(16, 185, 129, 0.08)",
                            border: "1px solid rgba(16, 185, 129, 0.25)",
                            borderRadius: "8px",
                            padding: "12px",
                            marginBottom: "14px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "8px",
                            }}
                          >
                            <span
                              style={{
                                fontSize: "12px",
                                fontWeight: 700,
                                color: "var(--accent-green)",
                              }}
                            >
                              Complimentary Quota Status
                            </span>
                            <span
                              className="badge"
                              style={{
                                background:
                                  remainingFreeSlots > 0
                                    ? "rgba(16, 185, 129, 0.2)"
                                    : "rgba(239, 68, 68, 0.2)",
                                color:
                                  remainingFreeSlots > 0
                                    ? "var(--accent-green)"
                                    : "var(--accent-red)",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              {remainingFreeSlots} Free Slot{remainingFreeSlots !== 1 ? "s" : ""} Available
                            </span>
                          </div>

                          <div
                            style={{
                              fontSize: "12px",
                              color: "var(--text-secondary)",
                              marginBottom: "12px",
                              lineHeight: "1.6",
                            }}
                          >
                            <div>
                              • Assigned Websites:{" "}
                              <strong style={{ color: "var(--text-primary)" }}>
                                {currentAssigned}
                              </strong>
                            </div>
                            <div>
                              • Complimentary Quota:{" "}
                              <strong style={{ color: "var(--text-primary)" }}>
                                {complimentaryLimit} websites
                              </strong>{" "}
                              (1 base + {currentExtraClients} extra)
                            </div>
                            <div>
                              • Can Add Right Now:{" "}
                              <strong
                                style={{
                                  color:
                                    remainingFreeSlots > 0
                                      ? "var(--accent-green)"
                                      : "var(--accent-red)",
                                }}
                              >
                                {remainingFreeSlots} website{remainingFreeSlots !== 1 ? "s" : ""} at $0.00
                              </strong>
                            </div>
                          </div>

                          <div
                            style={{
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "var(--text-primary)",
                              marginBottom: "6px",
                            }}
                          >
                            Grant User Extra Free Website(s) to Add:
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: "6px",
                              flexWrap: "wrap",
                              marginBottom: "10px",
                            }}
                          >
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{
                                padding: "4px 10px",
                                fontSize: "11px",
                                height: "28px",
                                borderColor: "rgba(16, 185, 129, 0.4)",
                                color: "var(--accent-green)",
                                fontWeight: 600,
                              }}
                              onClick={() =>
                                handleGrantAdditionalFreeSlots(selectedClientForDetails.id, 1)
                              }
                              disabled={updatingExtraClients}
                            >
                              +1 Free Slot
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{
                                padding: "4px 10px",
                                fontSize: "11px",
                                height: "28px",
                                borderColor: "rgba(16, 185, 129, 0.4)",
                                color: "var(--accent-green)",
                                fontWeight: 600,
                              }}
                              onClick={() =>
                                handleGrantAdditionalFreeSlots(selectedClientForDetails.id, 2)
                              }
                              disabled={updatingExtraClients}
                            >
                              +2 Free Slots
                            </button>
                            <button
                              className="btn btn-secondary btn-sm"
                              style={{
                                padding: "4px 10px",
                                fontSize: "11px",
                                height: "28px",
                                borderColor: "rgba(16, 185, 129, 0.4)",
                                color: "var(--accent-green)",
                                fontWeight: 600,
                              }}
                              onClick={() =>
                                handleGrantAdditionalFreeSlots(selectedClientForDetails.id, 5)
                              }
                              disabled={updatingExtraClients}
                            >
                              +5 Free Slots
                            </button>
                          </div>

                          <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                            <input
                              className="input"
                              type="number"
                              min="1"
                              value={grantSlotsInput}
                              onChange={(e) =>
                                setGrantSlotsInput(Math.max(1, parseInt(e.target.value) || 1))
                              }
                              disabled={updatingExtraClients}
                              style={{
                                width: "65px",
                                height: "32px",
                                fontSize: "12px",
                                padding: "4px 8px",
                              }}
                            />
                            <button
                              className="btn btn-primary btn-sm"
                              style={{
                                padding: "4px 12px",
                                height: "32px",
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                              }}
                              onClick={() =>
                                handleGrantAdditionalFreeSlots(
                                  selectedClientForDetails.id,
                                  grantSlotsInput,
                                )
                              }
                              disabled={updatingExtraClients}
                            >
                              {updatingExtraClients
                                ? "Granting..."
                                : `＋ Grant ${grantSlotsInput} Free Slot${grantSlotsInput > 1 ? "s" : ""}`}
                            </button>
                          </div>
                        </div>

                        {/* Manual Lifetime Extra DB Slots */}
                        <details
                          style={{
                            fontSize: "11px",
                            color: "var(--text-muted)",
                            marginBottom: "14px",
                            cursor: "pointer",
                          }}
                        >
                          <summary style={{ userSelect: "none", outline: "none" }}>
                            Advanced: Set Lifetime Extra DB Slots
                          </summary>
                          <div
                            style={{
                              marginTop: "8px",
                              padding: "8px 10px",
                              background: "var(--bg-primary)",
                              borderRadius: "6px",
                              border: "1px solid var(--border)",
                            }}
                          >
                            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                              <span style={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                                extra_clients:
                              </span>
                              <input
                                className="input"
                                type="number"
                                min="0"
                                value={extraClientsInput}
                                onChange={(e) =>
                                  setExtraClientsInput(Math.max(0, parseInt(e.target.value) || 0))
                                }
                                disabled={updatingExtraClients}
                                style={{ width: "70px", height: "28px", fontSize: "11px" }}
                              />
                              <button
                                className="btn btn-secondary btn-sm"
                                style={{ padding: "2px 8px", height: "28px", fontSize: "11px" }}
                                onClick={() =>
                                  handleUpdateUserExtraClients(
                                    selectedClientForDetails.id,
                                    extraClientsInput,
                                  )
                                }
                                disabled={
                                  updatingExtraClients ||
                                  extraClientsInput === (selectedClientForDetails.extra_clients || 0)
                                }
                              >
                                Save
                              </button>
                            </div>
                            <span
                              style={{
                                fontSize: "10px",
                                color: "var(--text-muted)",
                                display: "block",
                                marginTop: "4px",
                              }}
                            >
                              Total free quota = 1 base + extra_clients.
                            </span>
                          </div>
                        </details>
                      </>
                    );
                  })()}

                  <div className="divider" style={{ margin: "14px 0" }} />

                  {/* Add Client Website Directly */}
                  <div className="details-item">
                    <div className="details-label">Give / Add Client Website Directly</div>
                    <div style={{ display: "flex", gap: "8px", alignItems: "center", marginTop: "6px" }}>
                      <input
                        className="input"
                        type="text"
                        placeholder="e.g. clientdomain.com"
                        value={newAgencyWebsite}
                        onChange={(e) => setNewAgencyWebsite(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddAgencyClientDirect(selectedClientForDetails.id);
                          }
                        }}
                        disabled={addingAgencyClient}
                        style={{ flex: 1, fontSize: "12px" }}
                      />
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ padding: "6px 12px", height: "34px", fontSize: "12px", whiteSpace: "nowrap" }}
                        onClick={() => handleAddAgencyClientDirect(selectedClientForDetails.id)}
                        disabled={addingAgencyClient || !newAgencyWebsite.trim()}
                      >
                        {addingAgencyClient ? "Adding..." : "＋ Add Client"}
                      </button>
                    </div>
                    <span style={{ fontSize: "11px", color: "var(--text-muted)", display: "block", marginTop: "4px" }}>
                      Instantly assigns client website to this agency (bypasses checkout).
                    </span>
                  </div>

                  <div className="divider" style={{ margin: "14px 0" }} />

                  {/* Client Websites List */}
                  <div>
                    <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "8px" }}>
                      Assigned Clients ({agencyClientsList.length})
                    </div>
                    {loadingAgencyClients ? (
                      <div style={{ textAlign: "center", padding: "16px 0", color: "var(--text-muted)", fontSize: "12px" }}>
                        <div className="spinner" style={{ width: "22px", height: "22px", margin: "0 auto 8px" }}></div>
                        Loading client websites...
                      </div>
                    ) : agencyClientsList.length === 0 ? (
                      <div style={{ textAlign: "center", padding: "14px 0", color: "var(--text-muted)", fontSize: "12px", background: "var(--bg-primary)", borderRadius: "6px" }}>
                        No client websites assigned yet.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", maxHeight: "200px", overflowY: "auto" }}>
                        {agencyClientsList.map((ac) => {
                          const cleanWeb = ac.website.replace(/^https?:\/\//, "").replace(/\/$/, "");
                          return (
                            <div
                              key={ac.id || ac.website}
                              style={{
                                background: "var(--bg-primary)",
                                border: "1px solid var(--border)",
                                borderRadius: "6px",
                                padding: "8px 10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: "8px"
                              }}
                            >
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                                <div style={{ fontSize: "12px", fontWeight: 600, color: "var(--text-primary)" }}>
                                  🌐 {cleanWeb}
                                </div>
                                <div style={{ fontSize: "10px", color: "var(--text-muted)", marginTop: "2px", display: "flex", gap: "6px" }}>
                                  {ac.property_id && <span style={{ color: "var(--accent-green)" }}>✓ GA4</span>}
                                  {ac.gsc_site_url && <span style={{ color: "var(--accent-cyan)" }}>✓ GSC</span>}
                                  {!ac.property_id && !ac.gsc_site_url && <span>Not connected</span>}
                                </div>
                              </div>
                              <button
                                className="btn btn-ghost btn-sm"
                                style={{ color: "var(--accent-red)", padding: "2px 6px", height: "26px", fontSize: "11px" }}
                                onClick={() => handleDeleteAgencyClientDirect(selectedClientForDetails.id, ac.website)}
                                disabled={deletingAgencyClientWebsite === ac.website}
                                title="Remove client website"
                              >
                                {deletingAgencyClientWebsite === ac.website ? "..." : "✕"}
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="switch-row">
                <div className="switch-label-group">
                  <span className="switch-title">Assign Admin Role</span>
                  <span className="switch-desc">Grants full access to the admin dashboard</span>
                </div>
                <label className="switch-control">
                  <input
                    type="checkbox"
                    checked={!!selectedClientForDetails.is_admin}
                    disabled={updatingRole}
                    onChange={(e) => handleUpdateClientRole(selectedClientForDetails.id, e.target.checked)}
                  />
                  <span className="switch-slider"></span>
                </label>
              </div>
            </div>
            <div className="details-sidebar-footer">
              <button
                className="btn btn-danger btn-sm"
                style={{ marginRight: "auto", padding: "8px 16px" }}
                onClick={() => setDeleteConfirmUser(selectedClientForDetails)}
                disabled={deletingUser || selectedClientForDetails.id === session?.user?.id || selectedClientForDetails.email === session?.user?.email}
                title={
                  selectedClientForDetails.id === session?.user?.id || selectedClientForDetails.email === session?.user?.email
                    ? "You cannot delete your own account"
                    : ""
                }
              >
                Delete User
              </button>
              <button className="btn btn-ghost" onClick={() => setSelectedClientForDetails(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirmUser && (
        <Modal
          title="Confirm User Deletion"
          onClose={() => setDeleteConfirmUser(null)}
          footer={
            <div style={{ display: "flex", gap: "10px", width: "100%", justifyContent: "flex-end" }}>
              <button className="btn btn-ghost" onClick={() => setDeleteConfirmUser(null)} disabled={deletingUser}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={() => handleDeleteUser(deleteConfirmUser.id)} disabled={deletingUser}>
                {deletingUser ? "Deleting..." : "Yes, Delete User"}
              </button>
            </div>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "12px", color: "var(--text-secondary)", fontSize: "14px", lineHeight: "1.5" }}>
            <p>
              Are you sure you want to delete the user <strong>{deleteConfirmUser.name}</strong>?
            </p>
            <p style={{ color: "var(--accent-red)", fontWeight: 500, display: "flex", alignItems: "flex-start", gap: "6px" }}>
              <span>⚠️</span>
              <span>This action is permanent and cannot be undone. All user data, including profiles and configurations, will be deleted.</span>
            </p>
          </div>
        </Modal>
      )}

      {/* Guest Scan Details Modal */}
      {selectedGuestScanForDetails && (
        <Modal
          title="Guest Scan Details"
          onClose={() => setSelectedGuestScanForDetails(null)}
          footer={
            <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
              <button
                className="btn btn-danger btn-sm"
                onClick={() => {
                  const s = selectedGuestScanForDetails;
                  setSelectedGuestScanForDetails(null);
                  setDeleteConfirmGuestScan(s);
                }}
              >
                Delete Record
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => setSelectedGuestScanForDetails(null)}
              >
                Close
              </button>
            </div>
          }
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                borderBottom: "1px solid var(--border)",
                paddingBottom: "14px",
              }}
            >
              <div
                className="client-avatar"
                style={{
                  background: getCompanyColor(selectedGuestScanForDetails.company),
                  width: "48px",
                  height: "48px",
                  fontSize: "18px",
                }}
              >
                {initials(selectedGuestScanForDetails.company || "Unknown")}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: "16px", fontWeight: 700 }}>
                  {selectedGuestScanForDetails.company || "Unknown Company"}
                </div>
                <a
                  href={
                    selectedGuestScanForDetails.website &&
                    (selectedGuestScanForDetails.website.startsWith("http://") ||
                      selectedGuestScanForDetails.website.startsWith("https://"))
                      ? selectedGuestScanForDetails.website
                      : `https://${selectedGuestScanForDetails.website}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "var(--accent-cyan)",
                    fontSize: "13px",
                    textDecoration: "none",
                  }}
                >
                  {selectedGuestScanForDetails.website} ↗
                </a>
              </div>
              {selectedGuestScanForDetails.overall_score !== null &&
                selectedGuestScanForDetails.overall_score !== undefined && (
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                      AI Score
                    </div>
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: 800,
                        color:
                          selectedGuestScanForDetails.overall_score >= 70
                            ? "var(--accent-green)"
                            : selectedGuestScanForDetails.overall_score >= 40
                            ? "var(--accent-orange)"
                            : "var(--accent-red)",
                      }}
                    >
                      {selectedGuestScanForDetails.overall_score}%
                    </div>
                  </div>
                )}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "12px",
              }}
            >
              <div>
                <div className="input-label" style={{ marginBottom: "4px" }}>
                  Scanned Date & Time
                </div>
                <div style={{ fontSize: "13px", color: "var(--text-primary)" }}>
                  {formatScanDate(selectedGuestScanForDetails.created_at)}
                </div>
              </div>
              <div>
                <div className="input-label" style={{ marginBottom: "4px" }}>
                  Business Type
                </div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "var(--text-primary)",
                    textTransform: "capitalize",
                  }}
                >
                  {selectedGuestScanForDetails.business_type || "Not specified"}
                </div>
              </div>
              <div>
                <div className="input-label" style={{ marginBottom: "4px" }}>
                  Location Structure
                </div>
                <div style={{ fontSize: "13px", color: "var(--text-primary)" }}>
                  {selectedGuestScanForDetails.multiple_locations === "single"
                    ? "Single location"
                    : selectedGuestScanForDetails.multiple_locations === "multiple"
                    ? "Multiple locations"
                    : selectedGuestScanForDetails.multiple_locations || "Not specified"}
                </div>
              </div>
              <div>
                <div className="input-label" style={{ marginBottom: "4px" }}>
                  Pricing Display
                </div>
                <div style={{ fontSize: "13px", color: "var(--text-primary)" }}>
                  {selectedGuestScanForDetails.display_pricing || "Not specified"}
                </div>
              </div>
            </div>

            <div>
              <div className="input-label" style={{ marginBottom: "6px" }}>
                Company Description
              </div>
              <div
                style={{
                  background: "var(--bg-elevated)",
                  padding: "12px",
                  borderRadius: "var(--radius-sm)",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  color: "var(--text-secondary)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {selectedGuestScanForDetails.description || "No description provided."}
              </div>
            </div>

            {selectedGuestScanForDetails.scan_data && (
              <div>
                <div className="input-label" style={{ marginBottom: "6px" }}>
                  Audit Summary
                </div>
                <div
                  style={{
                    background: "var(--bg-elevated)",
                    padding: "12px",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                  }}
                >
                  {selectedGuestScanForDetails.scan_data.overall_summary && (
                    <div>{selectedGuestScanForDetails.scan_data.overall_summary}</div>
                  )}
                  {selectedGuestScanForDetails.scan_data.reasons && (
                    <div>
                      <strong style={{ color: "var(--text-primary)" }}>Findings: </strong>
                      {Array.isArray(selectedGuestScanForDetails.scan_data.reasons)
                        ? selectedGuestScanForDetails.scan_data.reasons.slice(0, 3).join(" • ")
                        : String(selectedGuestScanForDetails.scan_data.reasons)}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Guest Scan Delete Confirmation Modal */}
      {deleteConfirmGuestScan && (
        <Modal
          title="Confirm Guest Scan Deletion"
          onClose={() => setDeleteConfirmGuestScan(null)}
          footer={
            <div style={{ display: "flex", gap: "10px", width: "100%", justifyContent: "flex-end" }}>
              <button
                className="btn btn-ghost"
                onClick={() => setDeleteConfirmGuestScan(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleDeleteGuestScan(deleteConfirmGuestScan.id)}
              >
                Yes, Delete Record
              </button>
            </div>
          }
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              color: "var(--text-secondary)",
              fontSize: "14px",
              lineHeight: "1.5",
            }}
          >
            <p>
              Are you sure you want to delete the guest scan record for{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {deleteConfirmGuestScan.company || deleteConfirmGuestScan.website}
              </strong>
              ?
            </p>
            <p
              style={{
                color: "var(--accent-red)",
                fontWeight: 500,
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
              }}
            >
              <span>⚠️</span>
              <span>This record will be permanently deleted from scan history.</span>
            </p>
          </div>
        </Modal>
      )}
    </>
  );
}
